
import { Box, useTheme } from "@mui/material";
import { tokens } from "../../../../theme";

import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import ButtonGroup from "../../../../components/theme/buttons/ButtonGroup";
import Shbutton from "../../../../components/theme/buttons/Shbutton";
import ShDataGrid2 from "../../../../components/theme/datagrid2/ShDataGrid2";
import ShLabel from "../../../../components/theme/label/ShLabel";
import ShLabelSkeleton from "../../../../components/theme/skeleton/ShLabelSkeleton";
import ShTextSkeleton from "../../../../components/theme/skeleton/ShTextSkeleton";
import Text from "../../../../components/theme/text/Text";
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import SidepanelLoader from "../../../../components/theme/sidepanel2/SidepanelLoader";
import { getShipmentStatusLabeldata } from "../../../../utils/labelColorText";
import { selectUserPermission } from "../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from '../../../../utils/permissions';
import { useSelector } from "react-redux";
import { SHIPMENTS_FILTER_KEYS } from "../../../../components/global/DataGrid/Filter";
import { useGetShipmentsQuery } from "../../../../newapi/order/shipmentsSlice";
import { useGetRelationLabelShipperProfilesSelectQuery } from "../../../../newapi/shipper/shipperprofileSlice";
import MCreateShipment from "../../../../components/global/Modals/MCreateShipment";
import MExportPackagingUsage from "../../../../components/global/Modals/MExportPackagingUsage";
import { useGetSettingsForRelationQuery } from "../../../../newapi/global/generalApiSlice";
import AddIcon from '@mui/icons-material/Add';
import SHQuickAction from "../../../../components/theme/Modal/SHQuickAction";
import { RELATION_ORDERS_DEFAULT } from "../../../../components/theme/datagrid3/groupingPresets/GroupingPresets";
import ShDataGrid3 from "../../../../components/theme/datagrid3/ShDataGrid3";
import { RELATION_ORDERS_SHIPMENTS } from "../family";
import { getTrackingLink } from "../../../../utils/shipping";

const SpOrderDetails = SidepanelLoader(() => import("../../../../components/global/Sidepanels/SpOrderDetails/SpOrderDetails"));
const SpReturnDetails = SidepanelLoader(() => import("../../../../components/global/Sidepanels/SpReturnDetails/SpReturnDetails"));
const SpShipmentDetails = SidepanelLoader(() => import("../../../../components/global/Sidepanels/SpShipmentDetails/SpShipmentDetails"));

const GridActions = (props) => {
    const theme = useTheme();
    const {t} = useTranslation();

    // Both triggers are optional, so the group only appears when there is something in it.
    if (!props.canCreate && !props.canExportPackaging) {
        return <></>;
    }

    return (
        <ButtonGroup version={2}>
            {props.canExportPackaging &&
                <Shbutton
                    className={"group-btn"}
                    color={"primaryGrey"}
                    onClick={() => props.handleExportOpen(true)}
                    endIcon={<DownloadOutlinedIcon/>}
                    variant="contained"
                >
                    {t('export_packaging_usage')}
                </Shbutton>
            }
            {props.canCreate &&
                <Shbutton
                    className={"group-btn"}
                    color={theme.palette.mode === "dark" ? "blue" : "primary"}
                    onClick={() => props.handleOpen(true)}
                    endIcon={<AddIcon/>}
                    variant="contained"
                >
                    {t('create_shipment')}
                </Shbutton>
            }
        </ButtonGroup>
    );
}

let gridOptions = {
    export:true,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
    filter: [SHIPMENTS_FILTER_KEYS.TYPE, SHIPMENTS_FILTER_KEYS.SHIPMENT_STATUS],
};

const AllShipments = (props) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [data, setData] = useState([]);
    const [isExtended, setIsExtended] = useState(false);
    const [spLoading, setSpLoading] = useState();
    const [returnIsExtended, setReturnIsExtended] = useState(false);
    const [returnSpLoading, setReturnSpLoading] = useState();

    
    const [config, setConfig] = useState();

    const hasModifyPrivilege = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.OUTBOUND_MANAGE])
    );

    // Load the relation's self-service label profiles on page load. When none are
    // available the "Create Shipment" trigger is not rendered at all.
    const { data: shipperProfiles = [], isLoading: profilesLoading } =
        useGetRelationLabelShipperProfilesSelectQuery();
    const canCreateShipment = !profilesLoading && shipperProfiles.length > 0;

    const [createOpen, setCreateOpen] = useState(false);
    const handleCreateOpen = () => setCreateOpen(true);
    const handleCreateClose = () => setCreateOpen(false);

    // The packaging material report is only offered to relations the warehouse enabled it for.
    // The endpoint checks the same setting: a hidden button is not an authorisation.
    const { data: relationSettings } = useGetSettingsForRelationQuery();
    const canExportPackaging = relationSettings?.find(
        setting => setting.key === "relation_packaging_usage_export"
    )?.value === "true";

    const [exportOpen, setExportOpen] = useState(false);
    const handleExportOpen = () => setExportOpen(true);
    const handleExportClose = () => setExportOpen(false);

    useEffect(() => {
        props.replaceView({
            ...props.config,
            gridOptions: gridOptions,
            mobileActions: <GridActions canCreate={canCreateShipment} handleOpen={handleCreateOpen} canExportPackaging={canExportPackaging} handleExportOpen={handleExportOpen}></GridActions>,
        });

        return () => {
        }
    },[canCreateShipment, canExportPackaging]);

    // The totals panel lives above the tab view, in the parent, and belongs to this tab only.
    // Report that this tab is open so it shows, and that it closed so it goes away again.
    useEffect(() => {
        props.onActiveChange?.(true);

        return () => props.onActiveChange?.(false);
    },[]);
        

    const [orderId, setOrderId] = useState();
    const orderIdRef = useRef(orderId);
    
    const getOrderDetails = (params) => {
        if (!params?.row?.order) return;
    
        const newOrderId = parseInt(params.row.order.id);
        // Only update state if the productId has actually changed
        if (orderIdRef.current !== newOrderId) {
            if(!spLoading) {
                setSpLoading(true);
            }
            setOrderId(newOrderId);
            orderIdRef.current = newOrderId; // Update the ref to the new value
        }
        setIsExtended(true);
    };

    const [shipmentId, setShipmentId] = useState();
    const shipmentIdRef = useRef(shipmentId);
    const [shipmentIsExtended, setShipmentIsExtended] = useState(false);
    const [shipmentSpLoading, setShipmentSpLoading] = useState();

    const getShipmentDetails = (params) => {
        if (!params?.row?.id || params.row.skeleton) return;

        const newShipmentId = parseInt(params.row.id);
        // Only update state if the shipmentId has actually changed
        if (shipmentIdRef.current !== newShipmentId) {
            if (!shipmentSpLoading) {
                setShipmentSpLoading(true);
            }
            setShipmentId(newShipmentId);
            shipmentIdRef.current = newShipmentId; // Update the ref to the new value
        }
        setShipmentIsExtended(true);
    };

    const [returnId, setReturnId] = useState();
    const returnIdRef = useRef(orderId);

    const getReturnDetails = (params) => {
        if (!params?.row?.return) return;
    
        const newReturnId = parseInt(params.row.return.id);
        // Only update state if the productId has actually changed
        if (returnIdRef.current !== newReturnId) {
            if(!spLoading) {
                setReturnSpLoading(true);
            }
            setReturnId(newReturnId);
            returnIdRef.current = newReturnId; // Update the ref to the new value
        }
        setReturnIsExtended(true);
    };

    const columns = [
        {
            field: "trackingNumber",
            headerName: t("tracking_number"),
            flex:1,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                
                const trackingUrl = getTrackingLink(params.row);

                if (!trackingUrl) {
                    return params.row.trackingNumber;
                }

                return (
                <Box sx={{height:1,display:"flex",flexDirection:"column",justifyContent:"center"}} onClick={(event) => {event.stopPropagation(); window.open(trackingUrl, "_blank")}}>
                    <Text bold clickable={true} variant={theme.palette.mode === "dark" ? "blue" : "primary"}>{params.row.trackingNumber}</Text>
                </Box>);
            }
        },
        {field: "type", headerName: t("type"),flex:1, sortable: false,
            renderCell: (params) => {
            if(params.row.skeleton) {
                return <ShTextSkeleton/>
            }

            const handleClick = (event) => {
                let type = "";
                if (params.row.order && !params.row.return) {
                    type = "order";
                } else if (params.row.return) {
                    type = "return";
                }

                event.stopPropagation(); // Prevent click from propagating to the row
                if (type === "order") {
                    getOrderDetails(params);
                } else if (type === "return") {
                    getReturnDetails(params);
                } else {
                    getShipmentDetails(params);
                }
            };

            let description = "";
            if (params.row.order && !params.row.return) {
                description = `${t("order")} - ${params.row.order.webshopreference}`;
            } else if (params.row.return) {
                description = `${t("return")} - ${params.row.return.reference}`;
            } else {
                description = `${t("individual_shipment")}`;
            }

            if (params.row.order || params.row.return) {
                return (
                    <Box sx={{height:1,display:"flex",alignItems:'center'}} onClick={handleClick}>
                        <Text bold clickable={true} variant={theme.palette.mode === "dark" ? "blue" : "primary"}>{description}</Text>
                    </Box>
                );
            } else {
                return description;
            }
        }},
        {field: "name", headerName: t("name"),flex:1, sortable: false,
            renderCell: (params) => {
            if(params.row.skeleton) {
                return <ShTextSkeleton/>
            }

            let name = "";
            if (params.row.order) {
                name = params.row.order.deliveryName;
            } else if (params.row.return) {
                name = params.row.return.name;
            } else {
                name = params.row.companyName || params.row.name || "";
            }

            return name;
        }},
        {
            field: "address",
            headerName: t("address"),
            flex:2,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                
                return `${params.row.street} ${params.row.house_nr}${params.row.house_nr_extra}`;
            }
        },
        {
            field: "city",
            headerName: t("city"),
            flex:1,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                
                return params.row.city;
            }
        },
        {
            field: "postal",
            headerName: t("postal"),
            flex:1,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                
                return params.row.postal;
            }
        },
        {
            field: "country",
            headerName: t("country"),
            flex:1,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                
                return params.row.country?.toUpperCase();
            }
        },
        {
            field: "shipper",
            headerName: t("shipper"),
            flex:2,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }

                // Cirro hands the parcel on to another carrier, which is the one the relation
                // knows. Without a carrier behind it, or with only Cirro named again, there is
                // nothing to show.
                if (params.row.shipper.type === "cirro") {
                    const isCarrier = (value) => typeof value === "string" && value.trim() !== "" && value.trim().toLowerCase() !== "cirro";

                    if (isCarrier(params.row.externalShipperName)) {
                        return params.row.externalShipperName;
                    }

                    return isCarrier(params.row.externalShipperCode) ? params.row.externalShipperCode : t("unknown");
                }
                
                return `${params.row.shipper?.name} - ${params.row.shipperProfile?.name}`;
            },
            grouping: RELATION_ORDERS_DEFAULT.SHIPPER,
        },
        {
            field: "shipmentStatus", 
            headerName: t("shipment_status"),
            flex:1,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShLabelSkeleton/>
                }

                if (!params.row.shipmentStatus) {
                    return "";
                }

                const stateData = getShipmentStatusLabeldata(params.row.shipmentStatus, theme);
                let labelcolor = stateData.labelcolor;
                let labeltext = stateData.labeltext;

                // If there are shipper events, wrap the label with SHQuickAction
                if (params.row.shipperEvents && params.row.shipperEvents.length > 0) {
                    return (
                        <SHQuickAction
                            maxWidth={700}
                            component={
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        pt: 2,
                                        px: 2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: "relative",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 2,
                                        }}
                                    >
                                        <Text
                                            bold
                                            sx={{ width: "140px", flexShrink: 0 }}
                                        >
                                            {t("date")}
                                        </Text>
                                        <Text
                                            bold
                                            sx={{ width: "140px", flexShrink: 0 }}
                                        >
                                            {t("type")}
                                        </Text>
                                        <Text
                                            bold
                                            sx={{ minWidth: "350px", flexShrink: 0 }}
                                        >
                                            {t("status")}
                                        </Text>
                                    </Box>

                                    {params.row.shipperEvents.map((event, index) => (
                                        <Box
                                            key={index + "event"}
                                            sx={{
                                                position: "relative",
                                                minHeight: 40,
                                                py: 1,
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 2,
                                            }}
                                        >
                                            <Text sx={{ width: "140px", flexShrink: 0 }}>
                                                {event.timeStamp ? new Date(event.timeStamp).toLocaleString(i18n.language) : "-"}
                                            </Text>
                                            <Text sx={{ width: "140px", flexShrink: 0 }}>
                                                {event.type}
                                            </Text>
                                            <Text sx={{ minWidth: "350px", flexShrink: 0 }}>
                                                {event.status}
                                            </Text>
                                        </Box>
                                    ))}
                                </Box>
                            }
                        >
                            {({anchorEl, onMouseEnter, onMouseLeave, onTouchStart}) => (
                                <Box
                                    aria-owns={anchorEl ? "simple-popover" : undefined}
                                    aria-haspopup="true"
                                    onMouseEnter={onMouseEnter}
                                    onMouseLeave={onMouseLeave}
                                    onTouchStart={onTouchStart}
                                    sx={{
                                        display: "inline-flex",
                                    }}
                                >
                                    <ShLabel size={32} palette="normal" variant={labelcolor}>
                                        {t(labeltext)}
                                    </ShLabel>
                                </Box>
                            )}
                        </SHQuickAction>
                    );
                }
                
                return (
                    <ShLabel size={32} palette="normal" variant={labelcolor}>
                      {t(labeltext)}
                    </ShLabel>
                  );
              }
        },
        {
            field: "shipmentDate", 
            headerName: t("shipment_date"),
            flex:1,
            shFilter: {
                type: "date"
            },
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }

                let date = params.row.shipmentDate && params.row.shipmentDate !== null ? new Date(params.row.shipmentDate) : undefined;
              
                return (
                    <Text>
                        {date ? date.toLocaleDateString(i18n.language) : "-"}
                    </Text>
                );
            }
        },
        {
            field: "phoneView", 
            headerName: "PhoneView",
            cellClassName: "phone-column--cell",
            renderCell: (params) => {
                // WarehouseInvoiceCard
                // return (
                //     // <WarehouseInvoiceCard
                //     //     onClick={() => getInvoice(params)}
                //     //     skeleton={params.row.skeleton}
                //     //     {...params.row}
                //     // />
                //     // <ShTextSkeleton/>
                // )
            }
        },
    ];

    return (
        <Box height={1} display={"flex"} flexDirection={"column"}>
            {/* <GridActions></GridActions> */}
            <ShDataGrid3
                gridKey={RELATION_ORDERS_SHIPMENTS.SHIPMENTS.gridKey}
                enableGrouping={true}
                key={theme.palette.mode}
                content={useGetShipmentsQuery}
                config={config}
                gridActions={<GridActions canCreate={canCreateShipment} handleOpen={handleCreateOpen} canExportPackaging={canExportPackaging} handleExportOpen={handleExportOpen}></GridActions>}
                title={t('all')}
                gridOptions={gridOptions} 
                sortModel={{field: columns[9].field,type:"DESC"}}
                columns={columns}
                rows={data}
                onRowClick={getShipmentDetails}
                >
            </ShDataGrid3>

            {spLoading && 
                <SpOrderDetails
                    orderId={orderId}
                    isExtended={isExtended}
                    setIsExtended={setIsExtended}
                    // data={pickingJobData}
                /> 
            }
            {returnSpLoading &&
                <SpReturnDetails
                    returnId={returnId}
                    isExtended={returnIsExtended}
                    setIsExtended={setReturnIsExtended}
                    // data={pickingJobData}
                />
            }

            {shipmentSpLoading &&
                <SpShipmentDetails
                    shipmentId={shipmentId}
                    isExtended={shipmentIsExtended}
                    setIsExtended={setShipmentIsExtended}
                />
            }

            {canCreateShipment &&
                <MCreateShipment
                    open={createOpen}
                    handleClose={handleCreateClose}
                    profiles={shipperProfiles}
                />
            }

            {canExportPackaging &&
                <MExportPackagingUsage
                    open={exportOpen}
                    handleClose={handleExportClose}
                />
            }
        </Box>
    );
}

export default AllShipments;